import { CommonModule, DatePipe } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, DestroyRef, Input, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { Attachment, Bundle, DiagnosticReport, ImagingStudy, Observation, Provenance, Reference, Specimen } from 'fhir/r4';
import { imagingStudyExtensionsToImages, ImagingStudyLocalImage } from '../../shared/imaging-study-images/imaging-study-images.util';
import { backendEndPointToken } from '../../app.config';
import { DiagnosticReportPrintService } from '../../shared/diagnostic-report-print.service';

export type DiagnosticReportDetailsContext = {
  diagnosticReportId?: string;
  diagnosticReport?: DiagnosticReport;
};

@Component({
  selector: 'app-diagnostic-report-details',
  standalone: true,
  imports: [
    CommonModule,
    DatePipe,
    MatDialogModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDividerModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './diagnostic-report-details.component.html',
  styleUrl: './diagnostic-report-details.component.scss',
})
export class DiagnosticReportDetailsComponent {
  @Input() diagnosticReportId?: string;
  @Input() diagnosticReport?: DiagnosticReport;

  private readonly http = inject(HttpClient);
  private readonly backendApiBaseUrl = inject(backendEndPointToken);
  private readonly destroyRef = inject(DestroyRef);
  private readonly printService = inject(DiagnosticReportPrintService);
  readonly dialogRef = inject(MatDialogRef<DiagnosticReportDetailsComponent>, { optional: true });
  private readonly dialogData = inject<DiagnosticReportDetailsContext | null>(MAT_DIALOG_DATA, { optional: true });

  loading = true;
  loadError: string | null = null;

  report: DiagnosticReport | null = null;
  observations: Observation[] = [];
  specimens: Specimen[] = [];
  imagingStudies: ImagingStudy[] = [];
  provenance: Provenance[] = [];
  private resourceMap = new Map<string, any>();

  obsTableColumns: string[] = ['test', 'result', 'status', 'date', 'specimen'];
  specimenTableColumns: string[] = ['type', 'status', 'collected', 'received'];

  ngOnInit(): void {
    // Prefer explicit inputs, fall back to MAT_DIALOG_DATA when used as a dialog.
    const id = (this.diagnosticReportId ?? this.dialogData?.diagnosticReportId ?? '').toString().trim() || undefined;
    const dr = (this.diagnosticReport ?? this.dialogData?.diagnosticReport ?? null) as DiagnosticReport | null;

    if (dr) {
      this.diagnosticReportId = (dr.id ?? id ?? undefined) as any;
      this.loadFromReport(dr);
      return;
    }

    if (!id) {
      this.loading = false;
      this.loadError = 'DiagnosticReport context is missing.';
      return;
    }

    this.diagnosticReportId = id;

    this.http.get<DiagnosticReport>(`${this.backendApiBaseUrl}/DiagnosticReport/${encodeURIComponent(id)}`).pipe(
      catchError((e) => {
        console.error(e);
        this.loadError = 'Failed to load DiagnosticReport.';
        this.loading = false;
        return of(null as any);
      }),
      tap((r) => {
        if (r) this.report = r;
      }),
      switchMap((r) => (r ? this.loadReferences$(r) : of(null))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  close(): void {
    this.dialogRef?.close();
  }

  async print(): Promise<void> {
    if (!this.report) return;
    await this.printService.printDiagnosticReport(this.report, {
      title: this.reportTitle(this.report),
    });
  }

  private loadFromReport(dr: DiagnosticReport): void {
    this.report = dr;
    this.loadReferences$(dr)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private loadReferences$(dr: DiagnosticReport) {
    this.loading = true;
    this.loadError = null;

    // Use a single bundle request with _include for efficiency, similar to the print service pattern
    const id = dr.id;
    if (!id) {
      this.loading = false;
      return of(null);
    }

    const queryParts = [
      `_id=${encodeURIComponent(id)}`,
      '_include=DiagnosticReport:subject',
      '_include=DiagnosticReport:performer',
      // '_include=DiagnosticReport:resultsInterpreter',
      '_include=DiagnosticReport:result',
      '_include=DiagnosticReport:specimen',
      // '_include=DiagnosticReport:imagingStudy'
    ];
    const url = `${this.backendApiBaseUrl}/DiagnosticReport?${queryParts.join('&')}`;

    return this.http.get<Bundle>(url).pipe(
      switchMap(bundle => {
        const entries = bundle?.entry || [];
        this.resourceMap = new Map<string, any>();
        entries.forEach(e => {
          if (e.resource?.id) this.resourceMap.set(`${e.resource.resourceType}/${e.resource.id}`, e.resource);
        });

        const latest = this.resourceMap.get(`DiagnosticReport/${id}`) || dr;
        this.report = latest;

        this.observations = (latest.result || [])
          .map((ref: any) => this.resolveReferenceResource(ref))
          .filter((o: any) => !!o);

        this.specimens = (latest.specimen || [])
          .map((ref: any) => this.resolveReferenceResource(ref))
          .filter((s: any) => !!s);

        const imagingRefs = ((latest as any).imagingStudy || [])
          .map((ref: any) => ref?.reference)
          .filter((ref: any) => !!ref) as string[];

        const resolvedStudies = imagingRefs
          .map((ref) => this.resourceMap.get(ref))
          .filter((img: any): img is ImagingStudy => !!img);

        const missingRefs = imagingRefs.filter((ref) => !this.resourceMap.has(ref));
        if (!missingRefs.length) {
          this.imagingStudies = resolvedStudies;
          this.loading = false;
          return of(latest);
        }

        const requests = missingRefs.map((ref) => {
          const missingId = (ref || '').toString().split('/').pop() || '';
          return this.http.get<ImagingStudy>(`${this.backendApiBaseUrl}/ImagingStudy/${encodeURIComponent(missingId)}`).pipe(
            catchError(() => of(null as any))
          );
        });

        return forkJoin(requests).pipe(
          map((results) => {
            this.imagingStudies = resolvedStudies.concat((results.filter(Boolean) as ImagingStudy[]));
            this.loading = false;
            return latest;
          })
        );
      }),
      catchError((e) => {
        console.error(e);
        this.loading = false;
        this.loadError = 'Failed to load linked resources.';
        return of(null);
      })
    );
  }

  // ---------------- Display helpers ----------------
  reportTitle(dr: DiagnosticReport | null): string {
    if (!dr) return 'Diagnostic Report';
    return (
      dr?.code?.text ??
      dr?.code?.coding?.[0]?.display ??
      dr?.code?.coding?.[0]?.code ??
      'Diagnostic Report'
    );
  }

  statusLabel(status: any): string {
    const st = (status ?? 'unknown').toString();
    return st;
  }

  patientRef(dr: DiagnosticReport | null): string {
    return (dr as any)?.subject?.reference ?? '—';
  }

  patientDisplay(dr: DiagnosticReport | null): string {
    const subjectRef = (dr as any)?.subject ?? null;
    const subjectResource = this.resolveReferenceResource(subjectRef);
    if (subjectResource?.resourceType === 'Patient') {
      return this.humanName(subjectResource.name?.[0]) || subjectRef.display || subjectRef.reference || '—';
    }
    return subjectRef?.display || subjectRef?.reference || '—';
  }

  performerDisplay(dr: DiagnosticReport | null): string {
    const performerRef = ((dr as any)?.performer ?? [])[0] ?? null;
    const performerResource = this.resolveReferenceResource(performerRef);
    if (performerResource?.resourceType === 'Organization') {
      return performerResource.name || performerRef?.display || performerRef?.reference || '—';
    }
    if (performerResource?.resourceType === 'Practitioner') {
      return this.humanName(performerResource.name?.[0]) || performerRef?.display || performerRef?.reference || '—';
    }
    return performerRef?.display || performerRef?.reference || '—';
  }

  interpreterDisplay(dr: DiagnosticReport | null): string {
    const interpreterRef = ((dr as any)?.resultsInterpreter ?? [])[0] ?? null;
    const interpreterResource = this.resolveReferenceResource(interpreterRef);
    if (interpreterResource?.resourceType === 'Practitioner') {
      return this.humanName(interpreterResource.name?.[0]) || interpreterRef?.display || interpreterRef?.reference || '—';
    }
    return interpreterRef?.display || interpreterRef?.reference || '—';
  }

  private resolveReferenceResource(ref: Reference | null | undefined): any {
    if (!ref?.reference) return null;
    const reference = ref.reference.toString().trim();
    if (!reference) return null;

    const direct = this.resourceMap.get(reference);
    if (direct) return direct;

    const normalized = reference.split('?')[0].split('#')[0];
    const parts = normalized.split('/').filter(Boolean);
    if (parts.length >= 2) {
      const key = `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
      const normalizedResource = this.resourceMap.get(key);
      if (normalizedResource) return normalizedResource;
    }

    return null;
  }

  private humanName(name: any): string {
    if (!name) return '';
    const given = (name.given || []).join(' ');
    const family = name.family || '';
    return [given, family].filter(Boolean).join(' ').trim();
  }

  attachmentImages(dr: DiagnosticReport | null): Attachment[] {
    const list = (dr as any)?.presentedForm ?? [];
    const arr = Array.isArray(list) ? list : [list];
    const fromPresented = arr
      .filter((a: any) => {
        const ct = (a?.contentType ?? '').toString().toLowerCase();
        return ct.startsWith('image/') && (a?.data || a?.url);
      })
      .map((a: any) => a as Attachment);

    // Also include images referenced in linked ImagingStudy resources (extensions)
    const imagingAttachments: Attachment[] = [];
    try {
      const studies = Array.isArray(this.imagingStudies) ? this.imagingStudies : [];
      for (const s of studies) {
        const exts = Array.isArray((s as any)?.extension) ? (s as any).extension : [];
        const images = imagingStudyExtensionsToImages(exts || []);
        for (const img of images) {
          if (img.fileLink) {
            const att: any = { url: img.fileLink, contentType: img.contentType || 'image/*', title: img.fileName, size: img.size, creation: img.createdAt };
            if (img.azureBlobName) {
              att.extension = [ { url: 'https://elikita-server.daalitech.com/fhir/StructureDefinition/azureBlobName', valueString: img.azureBlobName } ];
            }
            imagingAttachments.push(att as Attachment);
          } else if (img.dataUrl) {
            const comma = img.dataUrl.indexOf(',');
            const base64 = comma >= 0 ? img.dataUrl.substring(comma + 1) : '';
            if (base64) imagingAttachments.push({ contentType: img.contentType || 'image/*', data: base64, title: img.fileName, size: img.size, creation: img.createdAt } as Attachment);
          }
        }
      }
    } catch {
      // ignore imaging conversion errors
    }

    return [...fromPresented, ...imagingAttachments];
  }

  presentedFormAttachments(dr: DiagnosticReport | null): Attachment[] {
    const list = (dr as any)?.presentedForm ?? [];
    const arr = Array.isArray(list) ? list : [list];
    return arr
      .filter((a: any) => a?.url || a?.data || a?.title || a?.contentType)
      .map((a: any) => a as Attachment);
  }

  attachmentHref(att: Attachment): string | null {
    if ((att as any)?.url) {
      return (att as any).url as string;
    }

    const data = (att as any)?.data;
    const contentType = (att?.contentType ?? 'application/octet-stream').toString();
    return data ? `data:${contentType};base64,${data}` : null;
  }

  attachmentLabel(att: Attachment): string {
    return (att?.title ?? att?.contentType ?? 'Document').toString();
  }

  attachmentTypeLabel(att: Attachment): string {
    return (att?.contentType ?? 'application/octet-stream').toString();
  }

  attachmentSizeLabel(att: Attachment): string {
    const size = Number((att as any)?.size ?? 0);
    if (!size) {
      return '';
    }
    if (size >= 1024 * 1024) {
      return `${(size / (1024 * 1024)).toFixed(1)} MB`;
    }
    if (size >= 1024) {
      return `${(size / 1024).toFixed(1)} KB`;
    }
    return `${size} bytes`;
  }

  asDataUrl(att: Attachment): string | null {
    const ct = (att?.contentType ?? '').toString();
    const data = (att as any)?.data;
    if (!ct || !data) return null;
    return `data:${ct};base64,${data}`;
  }

  public downloadAttachment = async (att: Attachment): Promise<void> => {
    const href = this.attachmentHref(att);
    if (!href) return;

    try {
      // If data URL, trigger direct download
      if (href.startsWith('data:')) {
        const a = document.createElement('a');
        a.href = href;
        a.download = this.attachmentLabel(att) || 'download';
        document.body.appendChild(a);
        a.click();
        a.remove();
        return;
      }

      // For cross-origin URLs (e.g., Cloudinary) the `download` attribute is ignored by browsers.
      // Attempt to fetch the resource as a blob and create an object URL to force a download.
      const resp = await fetch(href, { mode: 'cors', redirect: 'follow' });
      if (!resp.ok) {
        // fallback: open in new tab
        window.open(href, '_blank', 'noopener');
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Prefer a sensible filename: attachment.title, or try to derive from URL, or from content-disposition
      a.download = this.filenameForAttachment(att, href, resp) || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // If fetch is blocked by CORS or other errors, try Cloudinary fl_attachment fallback,
      // otherwise open the URL in a new tab.
      try {
        const rawHref = this.attachmentHref(att) || '';
        if (rawHref.includes('res.cloudinary.com')) {
          const fl = rawHref.replace('/upload/', '/upload/fl_attachment/');
          window.open(fl, '_blank', 'noopener');
          return;
        }
      } catch {
        // ignore
      }
      try { window.open(this.attachmentHref(att) || undefined, '_blank', 'noopener'); } catch { /* ignore */ }
    }
  }

  private filenameForAttachment(att: Attachment, href?: string, resp?: Response): string | null {
    // 1. Use explicit title
    let name = (att?.title ?? '').toString().trim();

    // 2. Try Content-Disposition header if available and exposed by CORS
    try {
      const cd = resp?.headers?.get?.('content-disposition') || resp?.headers?.get?.('Content-Disposition');
      if (cd) {
        const m = /filename\*=UTF-8''([^;\n]+)/i.exec(cd) || /filename="?([^";\n]+)"?/i.exec(cd);
        if (m && m[1]) {
          name = decodeURIComponent(m[1]);
        }
      }
    } catch {
      // ignore
    }

    // 3. Derive from URL if still empty
    if (!name && href) {
      try {
        const u = new URL(href);
        const part = u.pathname.split('/').pop() || '';
        name = decodeURIComponent(part.split('?')[0].split('#')[0]);
      } catch {
        // ignore
      }
    }

    if (!name) return null;

    // 4. Ensure an extension when contentType suggests one
    const ct = (att?.contentType ?? '').toString();
    const hasExt = /\.[a-z0-9]+$/i.test(name);
    if (!hasExt && ct) {
      const subtype = ct.split('/')[1] || '';
      if (subtype) {
        let ext = subtype.split('+')[0];
        if (ext === 'jpeg') ext = 'jpg';
        if (!/\./.test(name)) name = `${name}.${ext}`;
      }
    }

    return name;
  }

  obsTest(obs: Observation): string {
    return obs?.code?.text ?? obs?.code?.coding?.[0]?.display ?? obs?.code?.coding?.[0]?.code ?? '—';
  }

  obsResult(obs: Observation): string {
    const vq = (obs as any)?.valueQuantity;
    if (vq?.value != null) {
      const unit = vq?.unit ?? vq?.code ?? '';
      return `${vq.value}${unit ? ' ' + unit : ''}`;
    }
    return (
      (obs as any)?.valueString ??
      (obs as any)?.valueCodeableConcept?.text ??
      (obs as any)?.valueCodeableConcept?.coding?.[0]?.display ??
      (obs as any)?.valueBoolean ??
      '—'
    ).toString();
  }

  obsDate(obs: Observation): string | null {
    return ((obs as any)?.effectiveDateTime ?? (obs as any)?.issued ?? null) as any;
  }

  obsSpecimen(obs: Observation): string {
    const ref = (obs as any)?.specimen?.reference ?? (obs as any)?.specimen?.display ?? '—';
    return ref;
  }

  specimenType(sp: Specimen): string {
    return (sp as any)?.type?.text ?? (sp as any)?.type?.coding?.[0]?.display ?? (sp as any)?.type?.coding?.[0]?.code ?? '—';
  }

  specimenCollected(sp: Specimen): string | null {
    return ((sp as any)?.collection?.collectedDateTime ?? null) as any;
  }

  specimenReceived(sp: Specimen): string | null {
    return ((sp as any)?.receivedTime ?? null) as any;
  }

  imagingTitle(img: ImagingStudy): string {
    return (
      (img as any)?.description ??
      (img as any)?.procedureCode?.[0]?.text ??
      (img as any)?.procedureCode?.[0]?.coding?.[0]?.display ??
      `ImagingStudy ${(img as any)?.id ?? ''}`
    );
  }

  imagingStarted(img: ImagingStudy): string | null {
    return ((img as any)?.started ?? null) as any;
  }

  imagingThumbs(img: ImagingStudy): Attachment[] {
    const exts = Array.isArray((img as any)?.extension) ? (img as any).extension : [];
    return imagingStudyExtensionsToImages(exts || [])
      .map((item) => {
        if (item.fileLink) {
          return { url: item.fileLink, contentType: item.contentType || 'image/*', title: item.fileName, size: item.size, creation: item.createdAt } as Attachment;
        }
        if (item.dataUrl) {
          return { contentType: item.contentType || 'image/*', data: item.base64, title: item.fileName, size: item.size, creation: item.createdAt } as Attachment;
        }
        return null;
      })
      .filter((item): item is Attachment => !!item);
  }

  imagingModalities(img: ImagingStudy): string {
    const mods = ((img as any)?.modality ?? []) as any[];
    const labels = mods.map((m) => m?.display ?? m?.code).filter(Boolean);
    return labels.length ? labels.join(', ') : '—';
  }

  signatureLines(): Array<{ who: string; when: string | null; type: string }> {
    const lines: Array<{ who: string; when: string | null; type: string }> = [];

    // Fallback signature-like display from report participants
    if (this.report) {
      const performer = this.performerDisplay(this.report);
      if (performer && performer !== '—') lines.push({ who: performer, when: (this.report as any)?.issued ?? null, type: 'Performer' });

      const interp = this.interpreterDisplay(this.report);
      if (interp && interp !== '—') lines.push({ who: interp, when: (this.report as any)?.issued ?? null, type: 'Interpreter' });
    }

    // If server supports Provenance.signature, append those
    for (const p of (this.provenance ?? [])) {
      const sigs = (p as any)?.signature ?? [];
      for (const s of (Array.isArray(sigs) ? sigs : [])) {
        lines.push({
          who: (s?.who?.display ?? s?.who?.reference ?? '—').toString(),
          when: (s?.when ?? null) as any,
          type: 'Signature'
        });
      }
    }

    // de-dupe
    const key = (l: any) => `${l.type}::${l.who}::${l.when ?? ''}`;
    const mapBy = new Map<string, any>();
    for (const l of lines) {
      const k = key(l);
      if (!mapBy.has(k)) mapBy.set(k, l);
    }

    return Array.from(mapBy.values());
  }
}
